import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Card,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import apiService from '../../services/api';

const { Title } = Typography;

const ChannelList = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const response = await apiService.channels.getAll();
      setChannels(response.data);
    } catch (error) {
      message.error('Failed to fetch channels');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record._id);
    // Only populate fields that have actual values (not null, undefined, or empty strings)
    const populatedFields = {};
    Object.keys(record).forEach(key => {
      if (record[key] !== null && record[key] !== undefined && record[key] !== '') {
        populatedFields[key] = record[key];
      }
    });
    form.setFieldsValue(populatedFields);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await apiService.channels.delete(id);
      message.success('Channel deleted successfully');
      fetchChannels();
    } catch (error) {
      message.error('Failed to delete channel');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingId) {
        await apiService.channels.update(editingId, values);
        message.success('Channel updated successfully');
      } else {
        await apiService.channels.create(values);
        message.success('Channel created successfully');
      }
      setModalVisible(false);
      fetchChannels();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save channel');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      render: (email) => email || '-'
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      ellipsis: true,
      render: (phone) => phone || '-'
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (address) => address || '-'
    },
    {
      title: 'Account Manager',
      dataIndex: 'accountManager',
      key: 'accountManager',
      ellipsis: true,
      render: (accountManager) => accountManager || '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this channel?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Title level={4}>Channels</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Channel
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={channels}
          rowKey="_id"
          loading={loading}
        />

        <Modal
          title={editingId ? 'Edit Channel' : 'Add Channel'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: 'Please enter channel name' },
                { min: 2, max: 50, message: 'Name must be between 2 and 50 characters' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                { max: 200, message: 'Description must not exceed 200 characters' }
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: 'Phone number is required' },
                { pattern: /^[+]?[1-9][\d]{0,15}$/, message: 'Please enter a valid phone number' }
              ]}
            >
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item
              name="address"
              label="Address"
              rules={[
                { max: 500, message: 'Address must not exceed 500 characters' }
              ]}
            >
              <Input.TextArea rows={3} placeholder="Enter address" />
            </Form.Item>

            <Form.Item
              name="accountManager"
              label="Account Manager"
              rules={[
                { max: 100, message: 'Account manager must not exceed 100 characters' }
              ]}
            >
              <Input placeholder="Enter account manager name" />
            </Form.Item>

            <Form.Item
              name="isActive"
              valuePropName="checked"
              initialValue={true}
            >
              <Input type="checkbox" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingId ? 'Update' : 'Create'}
                </Button>
                <Button onClick={() => setModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default ChannelList; 